// Author: Ahsan Saleem
/**
 * SystemProvider — the operating environment's shared state: one facility
 * snapshot (so the desktop, launcher, inspectors and centres agree), the Bloom's
 * derived operating state, connectivity/sync, and recent objects. Refreshed on a
 * gentle interval so the system feels alive without hammering the API.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { auth, getFacilitySnapshot, getSites, getMe, type FacilitySnapshot, type Site, type Me } from '../api.js';
import { useOnline, useSync } from '../hooks.js';
import { session, type RecentObject } from '../session.js';
import type { BloomState } from './Bloom.js';

interface SystemValue {
  siteId: string;
  site: Site | undefined;
  user: Me | undefined;
  snapshot: FacilitySnapshot | undefined;
  loading: boolean;
  reload: () => void;
  bloomState: BloomState;
  online: boolean;
  sync: { pending: number; failed: number; lastSuccessAt?: string };
  recents: RecentObject[];
  addRecent: (o: Omit<RecentObject, 'at'>) => void;
}

const Ctx = createContext<SystemValue | null>(null);
export const useSystem = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSystem outside SystemProvider');
  return v;
};

export function SystemProvider({ children }: { children: ReactNode }) {
  const siteId = auth.siteId!;
  const online = useOnline();
  const sync = useSync();
  const [snapshot, setSnapshot] = useState<FacilitySnapshot>();
  const [site, setSite] = useState<Site>();
  const [user, setUser] = useState<Me>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [recents, setRecents] = useState<RecentObject[]>(session.recents());
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    if (!snapshot) setLoading(true);
    getFacilitySnapshot(siteId).then(
      (s) => { if (live.current) { setSnapshot(s); setLoading(false); } },
      () => { if (live.current) setLoading(false); },
    );
    return () => { live.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, tick]);

  // Identity of the current site and signed-in user, for the app chrome. The
  // site name is cached to auth so a reload paints the right label immediately.
  useEffect(() => {
    let on = true;
    getSites().then(
      (sites) => {
        if (!on) return;
        const current = sites.find((s) => s.id === siteId) ?? sites[0];
        if (current) { setSite(current); auth.siteName = current.name; }
      },
      () => {},
    );
    getMe().then((me) => { if (on) setUser(me); }, () => {});
    return () => { on = false; };
  }, [siteId]);

  // Gentle refresh; pause when the tab is hidden or offline.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) setTick((t) => t + 1);
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  const addRecent = useCallback((o: Omit<RecentObject, 'at'>) => {
    session.addRecent(o);
    setRecents(session.recents());
  }, []);

  const bloomState: BloomState = useMemo(() => {
    if (!online) return 'offline';
    if (!snapshot) return 'loading';
    const alerts = snapshot.alerts;
    if (alerts.some((a) => a.severity === 'CRITICAL')) return 'critical';
    const attention = alerts.length > 0 || snapshot.brief.attention.overdueTasks > 0 || snapshot.brief.attention.staleDatapoints > 0;
    return attention ? 'attention' : 'healthy';
  }, [online, snapshot]);

  const value = useMemo<SystemValue>(() => ({
    siteId, site, user, snapshot, loading, reload: () => setTick((t) => t + 1),
    bloomState, online, sync, recents, addRecent,
  }), [siteId, site, user, snapshot, loading, bloomState, online, sync, recents, addRecent]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
